import React, { createContext, useContext } from 'react';
import { get, keyBy } from 'lodash';
import { useFormikContext } from 'formik';

interface AdvancedFilterContextValue {
  initialCondition?: any;
  fields?: any[];
  fieldsByKey?: Record<string, any>;
  getFieldMetaByKey: (key: string) => any;
}

interface FilterConditionContextValue {
  fieldMeta?: any;
  conditionIndex?: any;
  getConditionValue: (field: string) => any;
  getConditionFieldPath: (field: string) => string;
  setConditionValue: (field: string, value: any) => any;
}

const AdvancedFilterContext = createContext<AdvancedFilterContextValue>(
  {} as AdvancedFilterContextValue,
);
const FilterConditionContext = createContext<FilterConditionContextValue>(
  {} as FilterConditionContextValue,
);

interface AdvancedFilterDropdownProviderProps {
  initialCondition?: any;
  fields?: any[];
  [key: string]: any;
}

/**
 * Advanced filter dropdown context provider.
 */
function AdvancedFilterDropdownProvider({
  initialCondition,
  fields,
  ...props
}: AdvancedFilterDropdownProviderProps) {
  const fieldsByKey = keyBy(fields, 'key');

  // Retrieve field meta by the given field key.
  const getFieldMetaByKey = React.useCallback(
    (key: string) => get(fieldsByKey, key),
    [fieldsByKey],
  );
  // Provider payload.
  const provider = { initialCondition, fields, fieldsByKey, getFieldMetaByKey };

  return <AdvancedFilterContext.Provider value={provider} {...props} />;
}

/**
 * Filter condition row context provider.
 */
function FilterConditionProvider({
  conditionIndex,
  ...props
}: {
  conditionIndex?: any;
  [key: string]: any;
}) {
  const { setFieldValue, values } = useFormikContext<any>();
  const { getFieldMetaByKey } = useAdvancedFilterContext();

  // Condition value path.
  const conditionPath = `conditions[${conditionIndex}]`;

  // Sets conditions value.
  const setConditionValue = React.useCallback(
    (field: string, value: any) => {
      return setFieldValue(`${conditionPath}.${field}`, value);
    },
    [conditionPath, setFieldValue],
  );

  // Retrieve condition field value.
  const getConditionValue = React.useCallback(
    (field: string) => get(values, `${conditionPath}.${field}`),
    [conditionPath, values],
  );

  // The current condition field meta.
  const fieldMeta = React.useMemo(
    () => getFieldMetaByKey(getConditionValue('fieldKey')),
    [getFieldMetaByKey, getConditionValue],
  );

  // Retrieve the condition field path.
  const getConditionFieldPath = React.useCallback(
    (field: string) => `${conditionPath}.${field}`,
    [conditionPath],
  );

  // Provider payload.
  const provider = {
    fieldMeta,
    conditionIndex,
    getConditionValue,
    getConditionFieldPath,
    setConditionValue,
  };
  return <FilterConditionContext.Provider value={provider} {...props} />;
}

const useFilterCondition = () => useContext(FilterConditionContext);
const useAdvancedFilterContext = () => useContext(AdvancedFilterContext);

export {
  AdvancedFilterDropdownProvider,
  FilterConditionProvider,
  useAdvancedFilterContext,
  useFilterCondition,
};
